import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Award, FileCheck } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { SOA_ROWS, QUALIFICATIONS } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const Certifications = () => {
  const { lang, t } = useLang();
  const cols = t("certifications.soaCols");

  return (
    <section
      id="certificazioni"
      data-testid="certifications-section"
      className="relative bg-cream text-forest py-24 sm:py-32 lg:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="max-w-3xl mb-16">
          <SectionLabel>{t("certifications.label")}</SectionLabel>
          <Reveal delay={0.1}>
            <h2 className="font-display font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl mt-6">
              {t("certifications.title")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-forest/70 font-light leading-relaxed mt-6">
              {t("certifications.intro")}
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* SOA table */}
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="bg-cream-2 rounded-tr-[2.5rem] rounded-bl-[2.5rem] p-8 lg:p-10 h-full">
              <div className="flex items-center gap-3 mb-8">
                <Award className="text-gold-2" size={24} />
                <h3 className="font-display text-2xl font-medium">{t("certifications.soaTitle")}</h3>
              </div>
              <div className="grid grid-cols-12 pb-3 border-b border-forest/20 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-forest/50">
                <span className="col-span-3">{cols[0]}</span>
                <span className="col-span-6">{cols[1]}</span>
                <span className="col-span-3 text-right">{cols[2]}</span>
              </div>
              {SOA_ROWS.map((r, i) => (
                <motion.div
                  key={r.cat}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: EASE, delay: i * 0.08 }}
                  className="grid grid-cols-12 items-center py-5 border-b border-forest/10"
                >
                  <span className="col-span-3 font-display text-2xl font-medium text-forest-deep">{r.cat}</span>
                  <span className="col-span-6 text-sm text-forest/70 leading-snug pr-2">{r[lang]}</span>
                  <span className="col-span-3 text-right">
                    <span className="inline-block rounded-full bg-forest text-cream text-xs font-semibold px-3 py-1">
                      {r.cls}
                    </span>
                  </span>
                </motion.div>
              ))}
            </div>
          </Reveal>

          {/* ISO + qualifications */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <Reveal delay={0.15}>
              <div className="bg-forest-deep text-cream rounded-tr-[2.5rem] rounded-bl-[2.5rem] p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="text-gold" size={24} />
                  <h3 className="font-display text-2xl font-medium">{t("certifications.isoTitle")}</h3>
                </div>
                <p className="text-cream/75 font-light leading-relaxed text-sm">
                  {t("certifications.isoText")}
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="bg-cream-2 rounded-tr-[2.5rem] rounded-bl-[2.5rem] p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <FileCheck className="text-gold-2" size={22} />
                  <h3 className="font-display text-xl font-medium">{t("certifications.qualTitle")}</h3>
                </div>
                <ul className="space-y-4">
                  {QUALIFICATIONS.map((q) => (
                    <li key={q.en} className={`flex gap-3 text-sm ${q.highlight ? "pt-4 border-t border-forest/10" : ""}`}>
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${q.highlight ? "bg-gold" : "bg-gold-2"}`} />
                      <div>
                        <div className={`font-medium leading-snug ${q.highlight ? "text-forest-deep" : "text-forest"}`}>{q[lang]}</div>
                        <div className="text-forest/50 text-xs mt-0.5">{q.org}</div>
                        {q[`note_${lang}`] && (
                          <div className="text-forest/60 text-xs mt-1.5 leading-relaxed italic">{q[`note_${lang}`]}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Certifications;
